<script>
import axios from 'axios'
import moment from 'moment'
import isEmpty from 'lodash/isEmpty'
import api from '@/helpers/api'
import { required, integer, minValue, maxValue } from 'vuelidate/lib/validators'
export default {
  name: 'file_upload',
  data: () => ({
    progress: 0,
    isUploading: false,
    file: null,
    upload_url: null,
    file_id: null,
    errorMsg: null,
    isUploaded: false
  }),
  props: {
    manualUpload: { type: Boolean, default: false },
    directory: { type: String, default: 'uploads' },
    allowedExtensions: { type: Array, default: () => [] },
    maxSizeMb: { type: Number, default: 16 },
    value: { type: String }
  },
  watch: {
    value: {
      async handler (v) {
        if (v) {
          let item = await api.files.fetch({ id: v })

          const {
            id,
            file_name: name,
            file_size: size,
            file_content_type: type,
            uploaded_at
          } = item
          this.file = { name, size, type }
          this.file_id = id
          this.isUploaded = !isEmpty(uploaded_at)
        }
      },
      immediate: true
    }
  },

  validations () {
    let maxSize = this.maxSizeMb * 1048576 // 1048576b = 1MB
    return {
      file: {
        size: {
          required,
          maxValue: maxValue(maxSize)
        },
        name: {
          required,
          extension (v) {
            if (!v || this.allowedExtensions.length === 0) return true
            let extension = v.replace(/^.*\./, '.')
            return this.allowedExtensions.includes(extension)
          }
        }
      }
    }
  },
  computed: {
    inputValue () {
      return this.file
        ? `${this.file.name} (${this.$options.filters.prettyBytes(
            this.file.size
          )})`
        : null
    },
    isLoading () {
      return false
    },
    fileSelected () {
      return !!this.file
    },
    accept () {
      return this.allowedExtensions.join(',')
    },
    description () {
      if (this.$v.file.$error) return null
      let s = []
      if (this.maxSizeMb) s.push(`${s} Max. size is ${this.maxSizeMb}MB`)
      if (this.allowedExtensions.length)
        s.push(`You can only upload files with the following extensions
        ${this.allowedExtensions.join(', ')}`)

      return s.join(', ')
    }
  },
  methods: {
    onBrowseFileHandler () {
      if (!this.fileSelected) this.$refs['__file__'].click()
    },
    onFileChangedHandler (event) {
      const files = event.target.files
      if (files.length > 0) {
        this.file = files[0]
        this.$v.file.$touch()
      }
    },

    async getUploadUrl () {
      this.$v.file.$touch()
      if (this.$v.file.$error) return

      const { name, type, size } = this.file

      let { url, file_id } = await api.files.upload_url({
        file_name: name,
        file_content_type: isEmpty(type) ? 'application/octet-stream' : type,
        file_size: size,
        key: `${this.directory}`
      })

      this.upload_url = url
      this.file_id = file_id
    },

    async uploadFileAsync () {
      if (this.isUploaded) return Promise.resolve(true)
      if (isEmpty(this.file_id) || isEmpty(this.upload_url)) {
        await this.getUploadUrl()
      }
      await this.onStartUploadHandler()
    },

    async onStartUploadHandler () {
      if (!this.file || isEmpty(this.upload_url) || isEmpty(this.file_id))
        return Promise.resolve(true)
      const { name, type, size } = this.file
      var options = {
        headers: {
          'Content-Type': isEmpty(type) ? 'application/octet-stream' : type,
          'x-amz-acl': 'private'
        },
        onUploadProgress: event => {
          this.progress = Math.round((100 * event.loaded) / event.total)
        }
      }

      try {
        this.isUploading = true
        await axios.put(this.upload_url, this.file, options)

        await api.files.update({
          id: this.file_id,
          uploaded_at: moment().format()
        })

        this.$emit('input', this.file_id)
        this.isUploading = false
      } catch (error) {
        this.isUploading = false
        throw new Error(error)
      }
    },

    async onClearHandler () {
      this.$refs['__file__'].value = null
      if (this.file_id)
        await api.files.delete({
          id: this.file_id
        })
      this.$v.$reset()
      this.file = null
      this.progress = 0
      this.file_id = null
      this.upload_url = null
      this.errorMsg = null
      this.isUploaded = false

      this.$emit('input', null)
    }
  }
}
</script>
<template>
  <div>
    <input
      type="file"
      class="d-none"
      ref="__file__"
      :accept="accept"
      name="file_input"
      v-on:change="onFileChangedHandler"
    />
    <g-form-error-wrapper :validations="$v.file">
      <template v-slot="{ errors, invalid }">
        <g-text-input
          :label="$t('STL File')"
          :description="description"
          :value="inputValue"
          placeholder="Choose an stl file..."
          readonly
          :disabled="
            $attrs.disabled ||
              (progress < 100 && isUploading) ||
              progress === 100
          "
          v-on:click="onBrowseFileHandler"
          :error-messages="errors"
          :is-valid="!invalid"
        >
          <template v-slot:append>
            <template v-if="!fileSelected">
              <b-input-group-append>
                <b-btn
                  variant="primary"
                  v-on:click="onBrowseFileHandler"
                  :disabled="$attrs.disabled"
                  ><i class="bx bx-import mr-1"></i>
                  {{ $t('select file') }}
                </b-btn>
              </b-input-group-append>
            </template>
            <template v-else>
              <b-input-group-append>
                <b-btn
                  variant="link"
                  class="px-3 m-0 border border-left-0 bg-transparent border-input"
                  v-on:click="onClearHandler"
                  :disabled="$attrs.disabled || (progress < 100 && isUploading)"
                  ><i class="far fa-trash-alt text-danger"></i
                ></b-btn>
              </b-input-group-append>
              <b-input-group-append
                v-if="!($v.file.$invalid && $v.file.$dirty) && manualUpload"
              >
                <b-btn
                  variant="primary"
                  v-on:click="onStartUploadHandler"
                  :disabled="
                    $attrs.disabled ||
                      !upload_url ||
                      (progress < 100 && isUploading) ||
                      progress === 100
                  "
                  ><i
                    class="fas fa-spinner fa-spin mr-2"
                    v-if="progress < 100 && isUploading"
                  />
                  <i class="fa fa-upload mr-2" v-else></i>Upload</b-btn
                >
              </b-input-group-append>
            </template>
          </template>

          <template v-slot:errors>
            <div v-if="!$v.file.size.maxValue">
              Max. size is {{ maxSizeMb }}MB
            </div>
            <div v-if="!$v.file.name.extension">
              You can only upload files with the following extensions
              {{ allowedExtensions.join(', ') }}
            </div>
          </template>
        </g-text-input>
      </template>
    </g-form-error-wrapper>
  </div>
</template>
